/**
 * A receipt, on its way from the accounts back onto a job.
 *
 * The one flow in this integration that runs the other way. Everything else
 * pushes Roof Board's truth into QuickBooks; this reads QuickBooks' back.
 *
 * Jonathon, 2026-08-30, on where job receipts actually come from: "typically
 * last minute purchases or on the go purchases at stores." A crew buys
 * something at Home Depot, emails the receipt to the QuickBooks receipts
 * address, and it has to end up on the right job with the picture attached.
 *
 * The step nobody can skip is in the middle: **QuickBooks' emailed-receipt
 * queue is not in the Accounting API.** A receipt sits in Banking → Receipts
 * until somebody accepts it, and only then does it become a transaction
 * anything can read. So Kelsey accepts it in QuickBooks and tags it to the
 * job's project, and everything after that happens without a person.
 *
 * That last part works only because a job is a project. A receipt tagged to a
 * project names one job and cannot mean another; tagged to a bare customer it
 * would name up to three, and every receipt would need somebody to say which.
 *
 * Every receipt lands as an **other expense**. Jonathon, 2026-08-30: "it
 * doesn't have to go as a material order if that's easier." It is easier, and
 * it removes the only decision left in the flow.
 *
 * ADR 0089.
 */

import { type Cents, toCents } from "./money.js";

/**
 * A job, as the review list needs to show it.
 *
 * Enough to recognise which one is which — the number people say out loud, the
 * address they picture, and the customer's name.
 */
export interface ReceiptJobChoice {
  id: string;
  job_number?: number | string | null;
  address?: string | null;
  city?: string | null;
  customer?: string | null;
}

/**
 * A job, written the way Kelsey has to recognise it in a list.
 *
 * Job number first because that is what everybody says out loud, then the
 * customer, then the address — a customer with three jobs is exactly the case
 * this list exists to disambiguate, and the address is the only thing that
 * tells them apart.
 */
export function jobChoiceLabel(job: ReceiptJobChoice): string {
  const number = job.job_number == null ? "" : `#${job.job_number}`;
  const who = (job.customer ?? "").trim();
  const where = [job.address, job.city]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(", ");
  return [number, who, where].filter(Boolean).join(" — ");
}

/**
 * Which job a receipt most likely belongs to.
 *
 * Not a decision — a suggestion, put at the top of the list so the common case
 * is one click. Kelsey always chooses; nothing is assigned by guessing.
 *
 * Two signals, both weak on their own and both cheap:
 *
 * - The receipt's own text naming a job number, which happens when somebody
 *   writes it on the receipt or in the memo.
 * - The receipt's date falling inside a job's production window. A purchase
 *   made on the day a crew was on a roof is usually for that roof.
 */
export function suggestJobs(
  receipt: { description?: string | null; txn_date?: string | null },
  jobs: readonly (ReceiptJobChoice & {
    sends_at?: string | null;
    completed_at?: string | null;
  })[],
): ReceiptJobChoice[] {
  const text = (receipt.description ?? "").toLowerCase();
  const on = (receipt.txn_date ?? "").slice(0, 10);

  const scored = jobs.map((job) => {
    let score = 0;
    const number = job.job_number == null ? "" : String(job.job_number);
    // A job number written on the receipt is as close to certain as this gets.
    if (number && text.includes(number)) score += 100;

    // Bought while the job was in production.
    const from = (job.sends_at ?? "").slice(0, 10);
    const to = (job.completed_at ?? "").slice(0, 10);
    if (on && from && on >= from && (!to || on <= to)) score += 10;

    return { job, score };
  });

  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((s) => s.job);
}

/** What a receipt costs, from whatever QuickBooks called the number. */
export function receiptAmount(receipt: {
  amount_cents?: number | string | null;
}): Cents {
  const raw = receipt.amount_cents;
  if (raw == null) return 0 as Cents;
  return Math.round(Number(raw)) as Cents;
}

/** A receipt's amount, when it arrives from QuickBooks as decimal dollars. */
export function receiptAmountFromQbo(total: unknown): Cents {
  return toCents(total);
}

/**
 * What the job record made from a receipt is called.
 *
 * The vendor, because that is what somebody looking at the job wants to know —
 * "Home Depot", not "Purchase 1042". Falls back to whatever there is.
 */
export function receiptRecordName(receipt: {
  vendor_name?: string | null;
  description?: string | null;
}): string {
  const vendor = (receipt.vendor_name ?? "").trim();
  const description = (receipt.description ?? "").trim();
  return vendor || description || "Receipt";
}

/**
 * What the receipts screen says about one.
 *
 * `byHand` distinguishes the ordinary case from the exception, because the
 * difference is the whole measure of whether this is working: a receipt that
 * landed by itself was tagged to a project in QuickBooks, and one somebody had
 * to place was not.
 */
export function receiptNotice(
  state: "new" | "assigned" | "ignored",
  byHand = false,
): string {
  if (state === "assigned") {
    return byHand
      ? "Put on the job by hand, as an other expense."
      : "On its job as an other expense.";
  }
  if (state === "ignored") return "Set aside — not a job cost.";
  return "Tagged to no job in QuickBooks. Pick one, or set it aside.";
}

/**
 * Whether a receipt may be assigned to this job.
 *
 * The job must be sold, for the same reason a cost only reaches the accounts
 * from a sold job: an unsold job has no revenue in QuickBooks to set the cost
 * against, and a cost with no matching revenue is what makes a month look
 * wrong.
 */
export function receiptBlockers(job: {
  sends_at?: string | null;
  is_test?: boolean | null;
}): string[] {
  const blockers: string[] = [];
  if (!job.sends_at) {
    blockers.push(
      "This job has not been sold yet. Press the handoff on it first, or pick another job.",
    );
  }
  if (job.is_test) {
    blockers.push(
      "This is a practice job, and practice jobs stay out of the accounts.",
    );
  }
  return blockers;
}

/* -------------------------------------------------------------------------- *
 * What belongs in the list at all
 * -------------------------------------------------------------------------- */

/**
 * Which purchases are receipts, and which are just the company's bookkeeping.
 *
 * The list shipped reading every Purchase QuickBooks had, and QuickBooks calls
 * a great deal a Purchase: on 2026-09-07 the twenty-seven waiting were rent,
 * fuel, lunches, software subscriptions, bank fees, a phone bill and a business
 * licence. Not one was a job cost. Jonathon, that day: "this should only
 * [be] charges from home depot, lowes, hardware stores that aren't heely, abc,
 * qxo, central states, etc. because those places we have a credit account
 * with."
 *
 * **QuickBooks does record the payee**, and Jonathon was right to say so when
 * an earlier version of this comment claimed otherwise. `EntityRef` on a
 * Purchase is a real vendor — "Intuit Inc" and "QuickBooks Payments" are both
 * in that same list of twenty-seven — and a receipt captured from an email
 * usually names the merchant, because QuickBooks reads it off the receipt.
 *
 * What the payee cannot do on its own is find a store run somebody paid for
 * themselves. Most of those twenty-seven named Jonathon Yale or Samuel Sandoval
 * because they were **reimbursements**: the company was paying the person back,
 * so the person is the payee and the shop is only in the memo. That is exactly
 * the case this list exists for — "typically last minute purchases or on the go
 * purchases at stores" — so a list of shop names would miss the receipts that
 * matter most. The payee is used for what it is good at, ruling suppliers out;
 * the account decides the rest. Three questions, in the order they are cheapest
 * to ask:
 *
 * 1. **Is it a supplier we buy on account from?** Heely-Brown, ABC, QXO and
 *    Central States send an invoice at the end of the month; their materials
 *    already reach a job as a material order, and reach the accounts as a bill.
 *    A second copy arriving as a receipt would put the cost on twice.
 * 2. **Is it posted as a cost of goods sold?** This is the accountant's own
 *    answer to "is this a job cost", already recorded against every
 *    transaction, and it is what separates plywood from lunch. Overhead is
 *    posted to an expense account and never appears here.
 * 3. **Is there a receipt to look at?** A receipt emailed in and accepted
 *    carries its picture; a card charge that arrived down the bank feed does
 *    not, and there is nothing for anybody to check it against.
 *
 * Nothing is deleted by any of this — a purchase that fails a test is kept with
 * the reason it failed, so a receipt that never turned up can be accounted for.
 */
export type ReceiptSkipReason = "on_account" | "not_a_job_cost" | "no_image";

/** A supplier we buy from on account, as this rule needs to recognise one. */
export interface OnAccountVendor {
  name?: string | null;
  quickbooks_vendor_id?: string | null;
}

/** A purchase read out of QuickBooks, as much of it as this rule reads. */
export interface PulledPurchase {
  vendor_name?: string | null;
  vendor_id?: string | null;
  /** QuickBooks' own type for the account the line was posted to. */
  account_type?: string | null;
  /** Whether QuickBooks holds a picture of the receipt. */
  has_image?: boolean;
}

/**
 * QuickBooks' account type for a job cost.
 *
 * One string, compared as QuickBooks writes it. Materials bought for a roof are
 * a cost of goods sold and everything the company spends on itself is not,
 * which is the whole distinction this screen needed and never had.
 */
const JOB_COST_ACCOUNT_TYPE = "cost of goods sold";

/** Loose enough to match "ABC Supply" against "ABC SUPPLY #234". */
function sameVendor(a: string, b: string): boolean {
  const tidy = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const left = tidy(a);
  const right = tidy(b);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
}

/**
 * Why this purchase is not a receipt for the list — or null if it is one.
 *
 * The order is deliberate: an on-account supplier is reported as such even when
 * its purchase would also have failed a later test, because that is the answer
 * somebody asking "where did the Heely receipt go?" needs to hear.
 */
export function receiptSkipReason(
  purchase: PulledPurchase,
  onAccount: readonly OnAccountVendor[],
): ReceiptSkipReason | null {
  const vendorName = (purchase.vendor_name ?? "").trim();
  const vendorId = (purchase.vendor_id ?? "").trim();
  const isOnAccount = onAccount.some((supplier) => {
    const id = (supplier.quickbooks_vendor_id ?? "").trim();
    if (id && vendorId && id === vendorId) return true;
    const name = (supplier.name ?? "").trim();
    return Boolean(name && vendorName && sameVendor(vendorName, name));
  });
  if (isOnAccount) return "on_account";

  const type = (purchase.account_type ?? "").trim().toLowerCase();
  if (type !== JOB_COST_ACCOUNT_TYPE) return "not_a_job_cost";

  if (!purchase.has_image) return "no_image";

  return null;
}

/** Why a purchase was passed over, in words, for anybody who goes looking. */
export function receiptSkipNotice(reason: ReceiptSkipReason): string {
  if (reason === "on_account")
    return "A supplier we buy from on account — their materials reach the job as a material order.";
  if (reason === "not_a_job_cost")
    return "Not posted as a cost of goods sold in QuickBooks, so it is overhead rather than a job cost.";
  return "No receipt attached in QuickBooks — a card charge off the bank feed, not a receipt somebody sent in.";
}

/* -------------------------------------------------------------------------- *
 * Where each pull starts reading
 * -------------------------------------------------------------------------- */

/**
 * The pull asks QuickBooks for purchases dated on or after a day, and the day
 * used to be the newest purchase date already recorded. On 2026-09-28 one
 * purchase arrived dated 2027-08-26 — a $10 meal with the year typed wrong —
 * and from then on every run asked for purchases dated after August 2027,
 * found none, and the copy of QuickBooks stopped moving with nothing on
 * screen to say so.
 *
 * So a date in the future is not a place to start from, and each pull looks
 * back a month from the newest real one. The look-back also catches a purchase
 * entered late with an earlier date, which reading strictly forward missed.
 * Reading a purchase twice costs nothing: each is recorded once, by its
 * QuickBooks id, and one already seen is left exactly as it is.
 *
 * The Edge Function carries a copy in `supabase/functions/quickbooks/receipts.ts`,
 * held to this one by `supabase/test/quickbooks-receipts-since-match.test.ts`.
 */
export const RECEIPT_LOOKBACK_DAYS = 30;

/**
 * The first purchase date to ask QuickBooks for.
 *
 * `newest` is the newest purchase date already recorded, or null before the
 * first run; `today` is the date in Birmingham. Both are `YYYY-MM-DD`.
 */
export function receiptsSince(newest: string | null, today: string): string {
  const from = newest !== null && newest <= today ? newest : today;
  const day = new Date(`${from}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - RECEIPT_LOOKBACK_DAYS);
  return day.toISOString().slice(0, 10);
}
